import { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../firebase";
import { getData } from "../Firebase/firestore";
import sh from "../assets/search.png";
// Same look as the fabric picker in Upload Product.
import "./main.css";
import "./resources.css";

// The fabric library (Fabric/Suits/fabrics, filled from Upload Fabric), loaded
// once per page and shared by every picker on it.
let libraryPromise = null;
export const loadFabricLibrary = () => {
    if (!libraryPromise) {
        libraryPromise = getData(db.collection("Fabric").doc("Suits").collection("fabrics"))
            .then((rows) => (rows || []).filter((f) => f && f.name))
            .catch(() => {
                libraryPromise = null;
                return [];
            });
    }
    return libraryPromise;
};

const colorsOf = (f) => (Array.isArray(f.color) ? f.color : String(f.color || "").split(","))
    .map((c) => String(c).trim())
    .filter(Boolean);

/** What a product keeps of a library fabric: enough to show and find it again. */
export const fabricRefOf = (f) => ({
    id: String(f.id || f.doc || ""),
    name: f.name || "",
    brand: f.brand || "",
    src: f.src || "",
    material: f.material || "",
    color: colorsOf(f).join(", "),
});

/** The text the Fabric field gets: the composition, else the fabric's name. */
export const fabricText = (f) => {
    const comp = String(f.material || "").trim();
    const w = String(f.weight || "").trim();
    const base = comp || f.name || "";
    return w && !/gsm/i.test(base) ? `${base}, ${w}${/^\d+$/.test(w) ? " GSM" : ""}` : base;
};

export const firstColorOf = (f) => colorsOf(f)[0] || "";

/** The slot under a piece: "Select Fabric", or the chosen fabric with Change / Remove. */
export function FabricSlot({ fabric, onOpen, onClear }) {
    if (!fabric) {
        return (
            <button type="button" className="preset-item-select-fabric-btn" onClick={onOpen}>
                <span>Select Fabric</span>
                <span className="preset-item-select-fabric-arrow">›</span>
            </button>
        );
    }
    return (
        <div className="preset-item-fabric-card">
            <div className="preset-item-fabric-thumb">
                {fabric.src ? <img src={fabric.src} alt={fabric.name || "Fabric"} /> : <div className="recom-button-card-image-fallback">No preview</div>}
            </div>
            <div className="preset-item-fabric-copy">
                <strong>{fabric.name || "Untitled"}</strong>
                <span>{[fabric.brand, fabric.material].filter(Boolean).join(" · ")}</span>
            </div>
            <div className="preset-item-fabric-actions">
                <button type="button" className="preset-item-fabric-change" onClick={onOpen}>Change</button>
                <button type="button" className="preset-item-fabric-clear" onClick={onClear}>Remove</button>
            </div>
        </div>
    );
}

function FilterSelect({ value, allLabel, options, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);
    const choose = (v) => {
        onChange(v);
        setOpen(false);
    };
    return (
        <div className="button-fabric-picker-filter" ref={ref}>
            <div className="fabric-custom-select">
                <button type="button" className="fabric-custom-select-trigger edit-fabric-select-trigger" onClick={() => setOpen((v) => !v)}>
                    <span>{value || allLabel}</span>
                    <span className={`fabric-custom-select-arrow ${open ? "open" : ""}`}>▾</span>
                </button>
                {open && (
                    <div className="fabric-custom-select-menu">
                        <button type="button" className={`fabric-custom-select-option ${value === "" ? "active" : ""}`} onClick={() => choose("")}>{allLabel}</button>
                        {options.map((o) => (
                            <button type="button" key={o} className={`fabric-custom-select-option ${value === o ? "active" : ""}`} onClick={() => choose(o)}>{o}</button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

/** The Upload Product fabric picker, for the fabric library. */
export default function FabricLibraryPicker({ item, selectedId, onPick, onClose }) {
    const [list, setList] = useState(null);
    const [q, setQ] = useState("");
    const [brand, setBrand] = useState("");
    const [type, setType] = useState("");

    useEffect(() => {
        let live = true;
        loadFabricLibrary().then((rows) => live && setList(rows));
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => {
            live = false;
            window.removeEventListener("keydown", onKey);
        };
    }, [onClose]);

    const brands = useMemo(() => Array.from(new Set((list || []).map((f) => f.brand).filter(Boolean))), [list]);
    const types = useMemo(() => Array.from(new Set((list || []).map((f) => f.fabricType).filter(Boolean))), [list]);
    const shown = useMemo(() => {
        const needle = q.toLowerCase().trim();
        return (list || []).filter((f) => {
            if (brand && String(f.brand || "").toLowerCase() !== brand.toLowerCase()) return false;
            if (type && String(f.fabricType || "").toLowerCase() !== type.toLowerCase()) return false;
            if (!needle) return true;
            return `${f.name} ${f.brand || ""} ${colorsOf(f).join(" ")} ${f.material || ""} ${f.pattern || ""} ${f.fabricType || ""}`.toLowerCase().includes(needle);
        });
    }, [list, q, brand, type]);

    return (
        <div className="recom-modal-backdrop" onClick={onClose}>
            <div className="recom-modal button-fabric-picker-modal" role="dialog" aria-modal="true" aria-label={`Select fabric for ${item}`} onClick={(e) => e.stopPropagation()}>
                <div className="recom-modal-header">
                    <div>
                        <h3>Select Fabric</h3>
                        <p>
                            Pick a fabric for <strong>{item}</strong>.{" "}
                            {list ? `${shown.length} fabric${shown.length === 1 ? "" : "s"} available.` : "Loading fabrics…"}
                        </p>
                    </div>
                    <button type="button" className="recom-modal-close" onClick={onClose} aria-label="Close">×</button>
                </div>

                <div className="recom-modal-filterbar">
                    <div className="button-fabric-picker-filters">
                        <FilterSelect value={brand} allLabel="All Brands" options={brands} onChange={setBrand} />
                        <FilterSelect value={type} allLabel="All Types" options={types} onChange={setType} />
                    </div>
                    <div className="recom-modal-search">
                        <input type="text" placeholder="Search fabric by name, brand, color..." value={q} onChange={(e) => setQ(e.target.value)} />
                        <img src={sh} alt="Search" />
                    </div>
                </div>

                <div className="recom-modal-body">
                    {list && shown.length === 0 ? (
                        <div className="recom-modal-empty">
                            No fabrics found{brand ? ` for brand ${brand}` : ""}{type ? ` of type ${type}` : ""}{q ? ` matching "${q}"` : ""}.
                        </div>
                    ) : (
                        <div className="button-fabric-picker-grid">
                            {shown.map((f) => {
                                const id = String(f.id || f.doc || "");
                                return (
                                    <div
                                        key={id || f.name}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={f.name}
                                        className={`outfit-fabric-card ${selectedId && selectedId === id ? "outfit-fabric-card-active" : ""}`}
                                        onClick={() => onPick(f)}
                                        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onPick(f)}
                                    >
                                        <img src={f.src} alt={f.name} className="outfit-fabric-card-image" />
                                        <div className="outfit-fabric-card-info">
                                            <h5>{f.name}</h5>
                                            <p>{f.brand || "-"}</p>
                                            <span>{colorsOf(f).join(", ") || "-"}{f.width ? ` · ${f.width}` : ""}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
